import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { Field } from 'react-final-form';

import { passAsIs } from 'utils/fn';
import { allowParamOmit } from 'utils/decorators';


function withFinalField({ type } = { }) {
  return (WrappedInput) => {
    class FFInput extends PureComponent {
      constructor(props) {
        super(props);
        const { meta: { error, initial } } = this.props;
        this.initialError = initial && error;
      }

      render() {
        const {
          input: { checked, name, value: originalValue, onChange, onFocus, onBlur },
          meta: { touched, error, submitError, dirtySinceLastSubmit, dirty, submitFailed },
          error: forcedError,
          valueProxy,
          onChangeProxy,
          onKeyDownProxy,
          errorProxy,
          ...inheritedProps
        } = this.props;
        const value = type === 'checkbox' || type === 'radio' ? checked : originalValue;

        const handleChange = typeof onChangeProxy === 'function' ? (val, event) =>
          onChange(onChangeProxy(val, event, value), event) : onChange;

        const handleKeyDown = ({ value, event }) => {
          if (typeof onKeyDownProxy !== 'function') return;

          const changedValue = onKeyDownProxy({ event, value, keyCode: event.keyCode });

          if (typeof changedValue === 'string') {
            handleChange(changedValue);
          }
        };

        const val = typeof valueProxy === 'function' ? valueProxy(value) : value;
        const err = typeof errorProxy === 'function' ? errorProxy(error) : error;

        /**
         * Error should not be shown initially on an empty field
         */
        const shouldShowError = touched && dirty || submitFailed || this.initialError;

        return (
          <WrappedInput
            {...inheritedProps}
            onFocus={onFocus}
            onBlur={onBlur}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            {...(typeof val !== 'undefined' && val !== null)
              ? { value: val }
              /** pass empty value for avoid transition from uncontrolled comp to controlled */
              : { value: '' }}
            name={name}
            /** There are three sources of errors - manually passed error, validation error and submission error */
            error={shouldShowError && (forcedError || err || !dirtySinceLastSubmit && submitError) || null}
            status={shouldShowError && (forcedError || err || !dirtySinceLastSubmit && submitError) && 'fail' || null}
          />
        );
      }
    }

    FFInput.propTypes = {
      valueProxy: PropTypes.func, /** to override value before passing it to underlying component */
      onChangeProxy: PropTypes.func, /** to override value before passing it back to redux forms */
      errorProxy: PropTypes.func,

      /** final-form props */
      input: PropTypes.shape({
        name: PropTypes.string,
        value: PropTypes.any,
        checked: PropTypes.bool,
        onChange: PropTypes.func,
        onFocus: PropTypes.func,
        onBlur: PropTypes.func,
      }),
      meta: PropTypes.shape({
        dirty: PropTypes.bool,
        touched: PropTypes.bool,
        submitFailed: PropTypes.bool,
        initial: PropTypes.any,
        error: PropTypes.string,
        submitError: PropTypes.string,
        dirtySinceLastSubmit: PropTypes.bool,
      }),

      // inheritedProps
      type: PropTypes.string,
      error: PropTypes.string,
    };

    /**
     * Override component name by prepending `FF~`
     * to make it look nice, for example: `FFTextInput`
     */
    if (process.env.NODE_ENV !== 'production') {
      const WrappedComponentName = WrappedInput.displayName || WrappedInput.name || 'Input';
      FFInput.displayName = `FF${WrappedComponentName}`;
    }

    const FFInputField = ({ value, ...props }) => (
      <Field
        component={FFInput}
        parse={passAsIs}
        format={passAsIs}
        allowNull
        type={type}
        {...{ [type === 'checkbox' ? 'checked' : 'value']: value }}
        {...props}
      />
    );

    /**
     * FFInputField has same props as Wrapped component
     */
    FFInputField.propTypes = {
      /** prop used for internal representation of checkbox */
      value: PropTypes.any,
      name: PropTypes.string.isRequired,
      /** inherited props */
    };

    /** furthermore, wrap all FF payFormFields with NestedFFContext, to support nesting */
    // return withFinalFormContext(FFInputField);
    return FFInputField;
  };
}

export default allowParamOmit(withFinalField);
